import { useMemo } from "react";

/**
 * DateInputMDY — locale-independent MM-DD-YYYY date picker. Renders three
 * selects (month / day / year) that read and write a canonical `YYYY-MM-DD`
 * string, so it's a drop-in replacement for `<input type="date">` without
 * inheriting the browser's system-locale display order.
 *
 *   <DateInputMDY value="2026-08-06" onChange={setV} yearsBack={2} yearsAhead={3} />
 */
export default function DateInputMDY({ value, onChange, yearsBack = 1, yearsAhead = 5, testid, disabled, className }) {
  const parsed = useMemo(() => parseISO(value), [value]);
  const now = new Date();
  const years = useMemo(() => {
    const base = now.getFullYear();
    const out = [];
    for (let y = base - yearsBack; y <= base + yearsAhead; y++) out.push(y);
    if (parsed.y && !out.includes(parsed.y)) out.push(parsed.y);
    return out.sort((a, b) => a - b);
  }, [yearsBack, yearsAhead, parsed.y, now]);
  const months = [
    { v: 1, l: "Jan" }, { v: 2, l: "Feb" }, { v: 3, l: "Mar" }, { v: 4, l: "Apr" },
    { v: 5, l: "May" }, { v: 6, l: "Jun" }, { v: 7, l: "Jul" }, { v: 8, l: "Aug" },
    { v: 9, l: "Sep" }, { v: 10, l: "Oct" }, { v: 11, l: "Nov" }, { v: 12, l: "Dec" },
  ];
  const daysInMonth = (m, y) => new Date(y, m, 0).getDate();
  const maxDay = daysInMonth(parsed.m || 1, parsed.y || now.getFullYear());
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);

  const push = (m, d, y) => {
    const dCap = Math.min(d, daysInMonth(m, y));
    onChange?.(`${y}-${pad(m)}-${pad(dCap)}`);
  };

  const baseSel = "h-9 bg-[#0A0A0A] border border-white/10 rounded-md px-2 text-sm outline-none focus:border-gold/40 disabled:opacity-60";

  return (
    <div className={`inline-flex items-center gap-1 ${className || ""}`} data-testid={testid || "date-input-mdy"}>
      <select data-testid={testid ? `${testid}-m` : "date-input-m"} aria-label="Month"
        value={parsed.m || 1} onChange={(e) => push(Number(e.target.value), parsed.d || 1, parsed.y || now.getFullYear())} disabled={disabled} className={baseSel}>
        {months.map((mo) => <option key={mo.v} value={mo.v}>{mo.l}</option>)}
      </select>
      <span className="text-zinc-500">/</span>
      <select data-testid={testid ? `${testid}-d` : "date-input-d"} aria-label="Day"
        value={parsed.d || 1} onChange={(e) => push(parsed.m || 1, Number(e.target.value), parsed.y || now.getFullYear())} disabled={disabled} className={baseSel}>
        {days.map((d) => <option key={d} value={d}>{pad(d)}</option>)}
      </select>
      <span className="text-zinc-500">/</span>
      <select data-testid={testid ? `${testid}-y` : "date-input-y"} aria-label="Year"
        value={parsed.y || now.getFullYear()} onChange={(e) => push(parsed.m || 1, parsed.d || 1, Number(e.target.value))} disabled={disabled} className={baseSel}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }
function parseISO(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  if (!m) return { y: 0, m: 0, d: 0 };
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}
